import React, { useEffect, useMemo, useRef, useState } from "react";

import AppIcon from "./Icon";

const DATA = [
  { harness: "Claude Code", model: "Fable 5", score: 0.81, cost: 9.03, minutes: 22.24 },
  { harness: "Claude Code", model: "Opus 4.8", score: 0.78, cost: 10.18, minutes: 22.0 },
  { harness: "Grok 4.5", model: "Grok 4.5", score: 0.78, cost: 0.0, minutes: 9.34 },
  { harness: "Codex", model: "GPT 5.6 Extra High", score: 0.75, cost: 4.35, minutes: 10.19 },
  { harness: "Cursor", model: "Composer 2.5", score: 0.75, cost: 0.83, minutes: 20.92 },
  { harness: "Cursor", model: "Composer 2.5 Fast", score: 0.74, cost: 3.11, minutes: 14.46 },
  { harness: "OpenCode", model: "GLM 5.2", score: 0.73, cost: 4.0, minutes: 27.0 },
  { harness: "Codex", model: "GPT 5.6 High", score: 0.72, cost: 3.57, minutes: 9.25 },
  { harness: "Claude Code", model: "Opus 4.7", score: 0.71, cost: 10.0, minutes: 20.55 },
  { harness: "Claude Code", model: "Opus 4.6", score: 0.62, cost: 9.0, minutes: 21.95 },
  { harness: "OpenCode", model: "MiniMax M2.7", score: 0.59, cost: 1.61, minutes: 23.87 },
  { harness: "Pi", model: "DeepSeek V4 Pro", score: 0.57, cost: 0.17, minutes: 15.76 },
  { harness: "Pi", model: "DeepSeek V4 Flash", score: 0.56, cost: 0.25, minutes: 17.26 },
  { harness: "Claude Code", model: "Haiku 4.5", score: 0.5, cost: 1.71, minutes: 16.95 },
].map((datum, index) => ({ ...datum, id: index }));

const COST_EXCLUDED_MODELS = new Set(["Grok 4.5"]);

const HARNESS_COLORS = {
  "Claude Code": "#FFA163",
  Codex: "#56D4BE",
  OpenCode: "#A3D65C",
  Pi: "#B891FF",
  "Grok 4.5": "#F2D264",
  Cursor: "#6FA8FF",
};

const HARNESS_ICONS = {
  "Claude Code": "claude",
  Codex: "openai",
  OpenCode: "opencode",
  Pi: "pi",
  "Grok 4.5": "grok",
  Cursor: "cursor",
};

const METRIC_CONFIG = {
  minutes: {
    xMin: 8,
    xMax: 32,
    ticks: [8, 12, 16, 20, 24, 28, 32],
    formatTick: (value) => `${value}m`,
    caption: "Duration",
    lowLabel: "<- slower",
    highLabel: "faster ->",
  },
  cost: {
    xMin: 0,
    xMax: 14,
    ticks: [0, 2, 4, 6, 8, 10, 12, 14],
    formatTick: (value) => `$${value}`,
    caption: "Cost",
    lowLabel: "<- pricier",
    highLabel: "cheaper ->",
  },
};

const MONO = "'SF Mono', 'IBM Plex Mono', ui-monospace, Menlo, Consolas, monospace";
const LABEL_HEIGHT = 26;
const FONT_SCALE = 2 / 3;
const Y_MIN = 0.45;
const Y_MAX = 0.85;

function fontSize(size) {
  return size * FONT_SCALE;
}

function paretoFrontier(points, metric) {
  const sorted = [...points].sort((a, b) => a[metric] - b[metric] || b.score - a.score);
  const frontier = [];
  let bestScore = -Infinity;

  sorted.forEach((point) => {
    if (point.score > bestScore) {
      frontier.push(point);
      bestScore = point.score;
    }
  });

  return frontier;
}

function collides(rect, placed) {
  return placed.some((other) => !(rect.x2 < other.x1 || rect.x1 > other.x2 || rect.y2 < other.y1 || rect.y1 > other.y2));
}

function rectFor(left, top, width) {
  return {
    x1: left - 2,
    y1: top - LABEL_HEIGHT / 2 - 1,
    x2: left + width + 2,
    y2: top + LABEL_HEIGHT / 2 + 1,
  };
}

function layoutLabels(points, plot) {
  const placed = [];
  const labels = {};
  const sorted = [...points].sort((a, b) => a.y - b.y || a.x - b.x);

  sorted.forEach((point) => {
    const base = { left: point.x - point.offset - point.labelWidth, top: point.y };
    let label = base;
    let guard = 0;

    while (collides(rectFor(label.left, label.top, point.labelWidth), placed) && guard < 260) {
      label = { ...label, top: label.top + 2 };
      guard += 1;
    }

    if (label.top > plot.bottom - 8) {
      label = base;
      while (collides(rectFor(label.left, label.top, point.labelWidth), placed) && guard < 520) {
        label = { ...label, top: label.top - 2 };
        guard += 1;
      }
    }

    placed.push(rectFor(label.left, label.top, point.labelWidth));
    labels[point.id] = label;
  });

  return labels;
}

function HtmlIcon({ name, size, color }) {
  return (
    <span style={{ color, width: size, height: size, display: "inline-flex", alignItems: "center", justifyContent: "center" }} aria-hidden="true">
      <AppIcon name={name} type="custom" className="benchmark-chart-icon" />
    </span>
  );
}

function SvgIcon({ name, size, color }) {
  return (
    <foreignObject width={size} height={size} style={{ overflow: "visible" }}>
      <div xmlns="http://www.w3.org/1999/xhtml" style={{ color, width: size, height: size, display: "flex" }} aria-hidden="true">
        <AppIcon name={name} type="custom" className="benchmark-chart-icon" />
      </div>
    </foreignObject>
  );
}

export default function StaticBenchmarkPageChart({ metric }) {
  const activeMetric = METRIC_CONFIG[metric] ? metric : "cost";
  const config = METRIC_CONFIG[activeMetric];
  const containerRef = useRef(null);
  const [width, setWidth] = useState(920);

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return undefined;

    const resizeObserver = new ResizeObserver((entries) => {
      setWidth(Math.max(620, Math.min(1200, entries[0].contentRect.width)));
    });
    resizeObserver.observe(element);

    return () => resizeObserver.disconnect();
  }, []);

  const height = width;
  const margin = { top: 28, right: 46, bottom: 108, left: 102 };
  const innerWidth = width - margin.left - margin.right;
  const innerHeight = height - margin.top - margin.bottom;
  const plot = {
    left: margin.left,
    right: margin.left + innerWidth,
    top: margin.top,
    bottom: margin.top + innerHeight,
  };
  const xScale = (value) => plot.left + (1 - (value - config.xMin) / (config.xMax - config.xMin)) * innerWidth;
  const yScale = (score) => plot.top + (1 - (score - Y_MIN) / (Y_MAX - Y_MIN)) * innerHeight;
  const yTicks = Array.from({ length: 9 }, (_, index) => Y_MIN + index * 0.05);

  const points = useMemo(
    () =>
      DATA.filter((datum) => activeMetric !== "cost" || !COST_EXCLUDED_MODELS.has(datum.model)).map((datum) => ({
        ...datum,
        x: xScale(datum[activeMetric]),
        y: yScale(datum.score),
        color: HARNESS_COLORS[datum.harness],
        icon: datum.model === "GLM 5.2" ? "pi" : HARNESS_ICONS[datum.harness],
        labelWidth: datum.model.length * 12.2 * FONT_SCALE + 6,
        offset: 21,
      })),
    [activeMetric, width],
  );
  const labels = useMemo(() => layoutLabels(points, plot), [points, plot.bottom, plot.left, plot.right, plot.top]);
  const frontier = useMemo(() => paretoFrontier(points, activeMetric), [points, activeMetric]);
  const frontierIds = useMemo(() => new Set(frontier.map((point) => point.id)), [frontier]);
  const frontierPath = useMemo(
    () =>
      frontier
        .slice()
        .sort((a, b) => a.x - b.x)
        .map((point, index) => `${index === 0 ? "M" : "L"}${point.x.toFixed(1)},${point.y.toFixed(1)}`)
        .join(" "),
    [frontier],
  );
  return (
    <div>
      <style>{`
        @keyframes benchmarkChartFade { from { opacity: 0 } to { opacity: 1 } }
        .benchmark-chart-fade { animation: benchmarkChartFade 420ms 240ms both; }
        .benchmark-chart-dot { transition: transform 620ms cubic-bezier(.4,0,.2,1); }
        .benchmark-chart-icon { width: 100%; height: 100%; display: block; fill: currentColor; }
        @media (prefers-reduced-motion: reduce) {
          .benchmark-chart-dot { transition: none; }
          .benchmark-chart-fade { animation: none; }
        }
      `}</style>
      <div
        ref={containerRef}
        style={{
          position: "relative",
          maxWidth: 1200,
          margin: "0 auto",
        }}
      >
        <svg
          width="100%"
          viewBox={`0 0 ${width} ${height}`}
          style={{ display: "block" }}
          role="img"
          aria-label={`Scatter chart of agent quality versus ${config.caption.toLowerCase()}.`}
        >
          <defs>
            <radialGradient id={`bestGlow-${activeMetric}`} cx="1" cy="0" r="1.35">
              <stop offset="0%" stopColor="#4ADE80" stopOpacity="0.07" />
              <stop offset="55%" stopColor="#4ADE80" stopOpacity="0.025" />
              <stop offset="100%" stopColor="#4ADE80" stopOpacity="0" />
            </radialGradient>
          </defs>

          <rect x={plot.left} y={plot.top} width={innerWidth} height={innerHeight} fill={`url(#bestGlow-${activeMetric})`} />

          {config.ticks.map((tick) => (
            <line key={`x-${tick}`} x1={xScale(tick)} x2={xScale(tick)} y1={plot.top} y2={plot.bottom} stroke="#272A31" strokeWidth="1" />
          ))}
          {yTicks.map((score) => (
            <line key={`y-${score}`} x1={plot.left} x2={plot.right} y1={yScale(score)} y2={yScale(score)} stroke="#272A31" strokeWidth="1" />
          ))}

          {config.ticks.map((tick) => (
            <text key={`xt-${tick}`} x={xScale(tick)} y={plot.bottom + 34} textAnchor="middle" fontSize={fontSize(20)} fontFamily={MONO} fill="#878D99">
              {config.formatTick(tick)}
            </text>
          ))}
          {yTicks.map((score) => (
            <text key={`yt-${score}`} x={plot.left - 14} y={yScale(score) + 6.5} textAnchor="end" fontSize={fontSize(20)} fontFamily={MONO} fill="#878D99">
              {Math.round(score * 100)}%
            </text>
          ))}
          <text x={plot.left + innerWidth / 2} y={height - 20} textAnchor="middle" fontSize={fontSize(21)} fill="#A5AAB5" fontFamily={MONO}>
            {config.caption}
          </text>
          <text x={plot.left} y={plot.bottom + 62} textAnchor="start" fontSize={fontSize(18)} fontFamily={MONO} fill="#5D626D">
            {config.lowLabel}
          </text>
          <text x={plot.right} y={plot.bottom + 62} textAnchor="end" fontSize={fontSize(18)} fontFamily={MONO} fill="#5D626D">
            {config.highLabel}
          </text>
          <text x={18} y={plot.top - 4} transform={`rotate(-90 18 ${plot.top + innerHeight / 2})`} textAnchor="middle" fontSize={fontSize(21)} fill="#A5AAB5" fontFamily={MONO}>
            <tspan x={18} y={plot.top + innerHeight / 2}>
              Quality
            </tspan>
          </text>
          <path className="benchmark-chart-fade" d={frontierPath} fill="none" stroke="#C7CBD4" strokeOpacity="0.35" strokeWidth="1.2" strokeDasharray="4 5" />

          <g className="benchmark-chart-fade">
            {points.map((point) => {
              const label = labels[point.id];
              if (!label) return null;

              const anchorX = label.left + point.labelWidth + 2;
              const displaced = Math.abs(label.top - point.y) > 8;

              return (
                <g key={point.id}>
                  {displaced && <line x1={point.x - 13} y1={point.y} x2={anchorX} y2={label.top} stroke={point.color} strokeOpacity="0.45" strokeWidth="1" />}
                  <text x={label.left} y={label.top + 7} fontSize={fontSize(20)} fontFamily={MONO} fill={point.color} style={{ pointerEvents: "none" }}>
                    {point.model}
                  </text>
                </g>
              );
            })}
          </g>

          {points.map((point) => {
            const iconSize = 21;

            return (
              <g key={point.id} className="benchmark-chart-dot" transform={`translate(${point.x} ${point.y})`}>
                {frontierIds.has(point.id) && <circle r="15" fill="none" stroke={point.color} strokeOpacity="0.45" strokeWidth="1" />}
                <g transform={`translate(${-iconSize / 2} ${-iconSize / 2})`}>
                  <SvgIcon name={point.icon} size={iconSize} color={point.color} />
                </g>
              </g>
            );
          })}
        </svg>
      </div>
      <div style={{ maxWidth: 1200, margin: "12px auto 0", display: "flex", flexWrap: "wrap", gap: "8px 16px", alignItems: "center" }}>
        {Object.entries(HARNESS_COLORS)
          .filter(([name]) => DATA.some((datum) => datum.harness === name))
          .map(([name, color]) => (
            <span key={name} style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: fontSize(14), fontFamily: MONO, color }}>
              <HtmlIcon name={HARNESS_ICONS[name]} size={15} color={color} />
              {name}
            </span>
          ))}
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: fontSize(14), fontFamily: MONO, color: "#A5AAB5" }}>
          <svg width="26" height="6" aria-hidden="true">
            <line x1="0" y1="3" x2="22" y2="3" stroke="#C7CBD4" strokeOpacity="0.5" strokeDasharray="4 4" />
          </svg>
          Pareto frontier
        </span>
      </div>
    </div>
  );
}
